import { Box } from '@mui/material'
import { C1BorderRadius } from 'c1ui/theme/C1BorderRadius'
import { FC, ReactNode, useEffect, useRef, useState } from 'react'

import { useFlyoutLabelId } from './NavItem'
import { navSidebarOverlayWidth } from './types'

type UserMenuOverlayProps = {
    children: ReactNode
    onRef?: (ref: HTMLDivElement | null) => void
}

/**
 * Compact overlay for the user menu that appears at the bottom of the sidebar.
 * Unlike SidebarOverlay, this is not full-height and aligns to the bottom.
 */
export const UserMenuOverlay: FC<UserMenuOverlayProps> = ({
    children,
    onRef,
}) => {
    const contentRef = useRef<HTMLDivElement>(null)
    const [left, setLeft] = useState<number | undefined>(undefined)
    const labelId = useFlyoutLabelId()

    useEffect(() => {
        const sidebar = document.getElementById('sidebar')
        const updatePosition = (): void => {
            if (sidebar) {
                setLeft(sidebar.getBoundingClientRect().right)
            }
        }
        updatePosition()

        const resizeObserver = new ResizeObserver(updatePosition)
        if (sidebar) resizeObserver.observe(sidebar)

        window.addEventListener('resize', updatePosition)
        return () => {
            window.removeEventListener('resize', updatePosition)
            resizeObserver.disconnect()
        }
    }, [])

    useEffect(() => {
        if (left !== undefined) {
            onRef?.(contentRef.current)
        }
        return () => onRef?.(null)
    }, [onRef, left])

    if (left === undefined) return null

    return (
        <Box
            className="nav-flyout"
            ref={contentRef}
            role="menu"
            aria-labelledby={labelId}
            sx={{
                position: 'fixed',
                left,
                bottom: 0,
                zIndex: 1250,
            }}
        >
            <Box
                className="nav-flyout-content"
                sx={{
                    width: navSidebarOverlayWidth + 80,
                    borderTopRightRadius: C1BorderRadius.large,
                    borderTop: '1px solid',
                    borderRight: '1px solid',
                    borderColor: 'divider',
                    backgroundColor: 'background.default',
                    overflow: 'hidden',
                    boxShadow: '0 0 30px rgba(0, 0, 0, 0.12)',
                }}
            >
                {children}
            </Box>
        </Box>
    )
}
